import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { type SignupRequestStep } from '@wellesley/openapi'
import { authToken } from './helpers/token'

export type SignupFields = {
  email?: string
  hash?: string
  nextStep?: SignupRequestStep
}
export type ForgotPasswordFields = {
  login?: string
}

export type ForgotPasswordStep = 'request' | 'passCode' | 'confirm' | 'login'
export type ChangePasswordStep = 'form' | 'passCode'
export type ChangeEmailStep = 'form' | 'passCode'
export type SignupStep =
  | 'rules'
  | 'signup'
  | 'passCode'
  | 'captcha'
  | 'approval'

export type AuthState = {
  forgotPasswordStep: ForgotPasswordStep
  forgotPasswordFields: ForgotPasswordFields | null
  signupStep: SignupStep
  signupFields: SignupFields | null
  changeEmailStep: ChangeEmailStep
  changePasswordStep: ChangePasswordStep
  newEmail: string | null
  token: string | null
  isPending: boolean
  isAuthenticated: boolean
  isAskLoginOpen: boolean
}

const initialState: AuthState = {
  forgotPasswordStep: 'request',
  forgotPasswordFields: null,
  signupStep: 'rules',
  signupFields: null,
  changeEmailStep: 'form',
  changePasswordStep: 'form',
  token: null,
  newEmail: null,
  isPending: !!authToken.getToken(),
  isAuthenticated: false,
  isAskLoginOpen: false,
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAskLoginOpen: (state, action: PayloadAction<boolean>) => {
      state.isAskLoginOpen = action.payload
    },
    login: (state, action: PayloadAction<string | undefined>) => {
      state.isAuthenticated = true
      state.token = action.payload || null
      state.isPending = false
    },
    logout: () => {
      return { ...initialState, isPending: false }
    },
    setNewEmail: (state, action: PayloadAction<string | null>) => {
      state.newEmail = action.payload
    },
    setChangeEmailStep: (state, action: PayloadAction<ChangeEmailStep>) => {
      state.changeEmailStep = action.payload
    },
    setChangePasswordStep: (
      state,
      action: PayloadAction<ChangePasswordStep>,
    ) => {
      state.changePasswordStep = action.payload
    },
    setForgotPasswordStep: (
      state,
      action: PayloadAction<ForgotPasswordStep>,
    ) => {
      state.forgotPasswordStep = action.payload
    },
    updateForgotPasswordFields: (
      state,
      action: PayloadAction<ForgotPasswordFields | null>,
    ) => {
      state.forgotPasswordFields = {
        ...state.forgotPasswordFields,
        ...action.payload,
      }
    },
    resetForgotPasswordFields: (state) => {
      state.forgotPasswordFields = null
    },
    setSignupStep: (state, action: PayloadAction<SignupStep>) => {
      state.signupStep = action.payload
    },
    updateSignupFields: (state, action: PayloadAction<SignupFields | null>) => {
      state.signupFields = { ...state.signupFields, ...action.payload }
    },
    resetSignupFields: (state) => {
      state.signupFields = null
    },
    setPending: (state, action: PayloadAction<boolean>) => {
      state.isPending = action.payload
    },
  },
  selectors: {
    selectAskLoginOpen: (state) => state.isAskLoginOpen,
    selectToken: (state) => state.token,
    selectNewEmail: (state) => state.newEmail,
    selectForgotPasswordStep: (state) => state.forgotPasswordStep,
    selectForgotPasswordFields: (state) => state.forgotPasswordFields,
    selectSignupStep: (state) => state.signupStep,
    selectSignupFields: (state) => state.signupFields,
    selectChangeEmailStep: (state) => state.changeEmailStep,
    selectChangePasswordStep: (state) => state.changePasswordStep,
    selectPending: (state) => state.isPending,
    selectIsAuthenticated: (state) => state.isAuthenticated,
  },
})
